import * as React from "react";
import { useSalesTable } from "./useSalesTable";
import { format } from "date-fns";
import { SaleStatusTypes } from "app/redux/salesSlice";
import { useBreakpoint } from "app/utils/useBreakpoint";
import { SalesCard } from "app/subframe";
import { Avatar } from "app/components/avatar/avatar";
import {
    Check,
    Clock,
    X,
    Eye,
    Download,
    Calendar as CalendarIcon,
    FileText,
    User,
} from "lucide-react";
import classNames from "classnames";
import {useEffect} from "react";
import {getSalePaymentBalances} from "../../utils/saleBalances";
import {useSelector} from "react-redux";
import type {RootState} from "app/redux/store";
import {shopTimeZoneFromShops, toLocalDate} from "app/utils/timezone";

// Walk-in sales are rung up without a client
const WALK_IN_LABEL = "Walk-in Customer";

export interface SalesTableProps {
    rowsPerPage: number;
    onSaleRowClick: (saleId: number) => void;
}

export const SalesTable: React.FC<SalesTableProps> = ({
                                                          rowsPerPage,
                                                          onSaleRowClick,
                                                      }) => {
    const {
        totalCount,
        page,
        handlePageChange,
        sortedItems,
    } = useSalesTable({ rowsPerPage });

    const { isBelowSm } = useBreakpoint("sm");
    const shops = useSelector((state: RootState) => state.shops) as any[];
    // The sale's day at the shop, not in the viewer's zone
    const shopDate = (value: string) => format(toLocalDate(value, shopTimeZoneFromShops(shops)), "MMM dd yyyy");

    const pages = Math.ceil(totalCount / rowsPerPage);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case SaleStatusTypes.CLOSED:
                return (
                    <>
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              Completed
            </span>
                    </>
                );
            case SaleStatusTypes.OPEN:
                return (
                    <>
                        <Clock className="w-4 h-4 text-yellow-600" />
                        <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              Pending
            </span>
                    </>
                );
            case SaleStatusTypes.VOID:
                return (
                    <>
                        <X className="w-4 h-4 text-red-600" />
                        <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
              Failed
            </span>
                    </>
                );
            case SaleStatusTypes.REFUNDED:
                return (
                    <>
                        <X className="w-4 h-4 text-orange-600" />
                        <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
              Refunded
            </span>
                    </>
                );
            default:
                return null;
        }
    };

    const getMethodIcon = (method: string) => {
        // Example: you can replace with actual icons for methods
        return <div className="w-4 h-4 bg-gray-300 rounded-full" />;
    };

    const formatCents = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;

    const transactionRows = React.useMemo(() => {
        return (sortedItems || []).flatMap((sale: any) => {
            const txs = (sale.paymentTransactions || []).filter(
                (t: any) => !(t.preAuth || t.pre_auth)
            );
            if (!txs.length) {
                return [{
                    sale,
                    rowKey: `sale-${sale.id}`,
                    amount: sale.total,
                    method: sale?.dualPricingMethod?.toUpperCase?.() || "",
                    date: sale.createdAt,
                    subtitle: `Order #${sale.id}`,
                    kindLabel: null,
                    status: sale.status,
                }];
            }
            const paidRows = txs.map((tx: any) => {
                const isDeposit = tx.isDeposit || tx.is_deposit;
                const tipCents = tx.tipsCents || tx.tips_cents || 0;
                return {
                    sale,
                    rowKey: `tx-${tx.id}`,
                    amount: formatCents(tx.totalCents || tx.total_cents),
                    method: (tx.paymentMethod || tx.payment_method || sale?.dualPricingMethod || "").toString().toUpperCase(),
                    date: tx.createdAt || tx.created_at || sale.createdAt,
                    subtitle: isDeposit ? "Deposit" : "Balance",
                    kindLabel: (isDeposit ? "Deposit" : "Payment") + (tipCents > 0 ? ` · incl. tip ${formatCents(tipCents)}` : ""),
                    // Each payment shows its own state: a refunded one reads "Refunded", not "Completed"
                    status: String(tx.status ?? "").toUpperCase() === "REFUNDED"
                        ? SaleStatusTypes.REFUNDED
                        : SaleStatusTypes.CLOSED,
                };
            });
            const { remainingCents } = getSalePaymentBalances(sale);
            if (remainingCents > 0) {
                paidRows.push({
                    sale,
                    rowKey: `sale-${sale.id}-remaining`,
                    amount: formatCents(remainingCents),
                    method: "",
                    date: sale.createdAt,
                    subtitle: "Balance",
                    kindLabel: "Remaining",
                    status: SaleStatusTypes.OPEN,
                });
            }
            return paidRows;
        });
    }, [sortedItems]);


    if (isBelowSm) {
        // Mobile version → Cards
        return (
            <div className="flex flex-col items-center gap-2 mb-36">
                {transactionRows.map((row: any) => (
                    <SalesCard
                        key={row.rowKey}
                        client={row.sale.client?.name || WALK_IN_LABEL}
                        date={shopDate(row.date)}
                        price={row.amount}
                        status={row.status}
                        avatarImg={row.sale.client?.avatar}
                        onClick={() => onSaleRowClick(row.sale)}
                    />
                ))}
                {/* Simple Pagination */}
                <div className="mt-6 flex gap-2">
                    {Array.from({ length: pages }).map((_, i) => (
                        <button
                            key={i}
                            onClick={() => handlePageChange(i + 1)}
                            className={classNames(
                                "px-3 py-1 border rounded",
                                page === i + 1 ? "bg-purple-600 text-white" : "bg-white"
                            )}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>
            </div>
        );
    }



    return (
        <div className="p-6">
            <table className="w-full">
                <thead>
                <tr>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="pb-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                {transactionRows.map((row: any) => (
                    <tr
                        key={row.rowKey}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => onSaleRowClick(row.sale)}
                    >
                        {/* Transaction */}
                        <td className="py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-gray-900">{row.sale.id}</div>
                                    <div className="text-xs text-gray-500">{row.subtitle}</div>
                                </div>
                            </div>
                        </td>

                        {/* Customer */}
                        <td className="py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                                <Avatar src={row.sale.client?.avatar} name={row.sale.client?.name || WALK_IN_LABEL} size={32} type="character" />
                                <div>
                                    <div className="text-sm font-medium text-gray-900">{row.sale.client?.name || WALK_IN_LABEL}</div>
                                    <div className="text-xs text-gray-500">
                                        {row.sale.client?.email || ""}
                                    </div>
                                </div>
                            </div>
                        </td>

                        {/* Amount */}
                        <td className="py-4 whitespace-nowrap">
                            <div>


                                <div className="text-sm font-semibold text-gray-900">{row.amount}</div>
                                <div className="text-xs text-gray-500">
                                    {row.kindLabel || (row.sale.paidTipsCents > 0 ? `Incl. tip: ${row.sale.paidTips}` : "No tip")}
                                </div>
                            </div>
                        </td>

                        {/* Payment Status */}
                        <td className="py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">{getStatusBadge(row.status)}</div>
                        </td>

                        {/* Payment Method */}
                        <td className="py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                                {row.method}
                            </div>
                        </td>

                        {/* Date */}
                        <td className="py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                                <CalendarIcon className="w-4 h-4 text-gray-400" />
                                <div>
                                    <div className="text-sm text-gray-900">{shopDate(row.date)}</div>
                                    <div className="text-xs text-gray-500">
                                        {row.sale.itemsCount || 1} item{row.sale.itemsCount > 1 ? "s" : ""}
                                    </div>
                                </div>
                            </div>
                        </td>

                        {/* Actions */}
                        <td className="py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                                <button className="p-2 text-purple-600 hover:text-purple-700">
                                    <Eye className="w-4 h-4" />
                                </button>
                                {/*<button className="p-2 text-purple-600 hover:text-purple-700">*/}
                                {/*    <Download className="w-4 h-4" />*/}
                                {/*</button>*/}
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Pagination */}
            {pages > 1 && (
                <div className="mt-6 flex gap-2 justify-center">
                    {Array.from({ length: pages }).map((_, i) => (
                        <button
                            key={i}
                            onClick={() => handlePageChange(i + 1)}
                            className={classNames(
                                "px-3 py-1 border rounded",
                                page === i + 1 ? "bg-purple-600 text-white" : "bg-white"
                            )}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
